import { useState } from "react";
import { Upload, Leaf } from "lucide-react";
import MasterCrud from "@/components/app/MasterCrud";
import ImportDialog from "@/components/app/ImportDialog";
import { IDR, NUM } from "@/lib/api";
import { canEditMaster, useAuth } from "@/lib/auth";

export default function Materials() {
  const { user } = useAuth();
  const [importOpen, setImportOpen] = useState(false);
  const [pricesOpen, setPricesOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const toolbar = canEditMaster(user) ? (
    <div className="flex flex-wrap items-center gap-2 ml-auto">
      <button onClick={() => setImportOpen(true)} className="gc-btn-ghost text-sm" data-testid="btn-import-materials"><Upload className="w-4 h-4" /> Import Bahan</button>
      <button onClick={() => setPricesOpen(true)} className="gc-btn-ghost text-sm" data-testid="btn-import-prices"><Upload className="w-4 h-4" /> Import Harga</button>
    </div>
  ) : null;

  return (
    <>
      <MasterCrud
        key={refreshKey}
        endpoint="/materials"
        title="Bahan Baku"
        description="Kelola daftar bahan baku, harga, atribut substitusi/daur ulang, dan faktor emisi untuk estimasi jejak karbon."
        fields={[
          { key: "code", label: "Kode Bahan", type: "text", required: true },
          { key: "name", label: "Nama Bahan", type: "text", required: true },
          { key: "category", label: "Kategori", type: "text", default: "Bahan Utama" },
          { key: "unit", label: "Satuan", type: "text", default: "kg", required: true },
          { key: "unit_price", label: "Harga Satuan (IDR)", type: "number", required: true },
          { key: "emission_factor", label: "Faktor Emisi (kg CO₂eq/unit)", type: "number", step: "0.0001", hint: "Wajib divalidasi mitra sebelum dipakai untuk klaim resmi." },
          { key: "emission_source", label: "Sumber Faktor Emisi", type: "text", hint: "Mis. IPCC 2019, EPD industri, hasil uji" },
          { key: "is_substitute", label: "Bahan Substitusi", type: "checkbox" },
          { key: "is_recycled", label: "Daur Ulang", type: "checkbox" },
          { key: "is_low_carbon", label: "Rendah Karbon", type: "checkbox" },
          { key: "notes", label: "Catatan", type: "textarea" },
          { key: "is_active", label: "Aktif", type: "checkbox", default: true },
        ]}
        columns={[
          { key: "code", header: "Kode" },
          { key: "name", header: "Nama" },
          { key: "unit", header: "Satuan" },
          { key: "unit_price", header: "Harga", align: "right", render: (r) => <span className="font-semibold text-slate-900">{IDR(r.unit_price)}</span> },
          { key: "emission_factor", header: "Faktor Emisi", align: "right", render: (r) => r.emission_factor > 0 ? (
              <div className="text-right">
                <div className="font-semibold text-emerald-700">{NUM(r.emission_factor, 4)} <span className="text-[10px] text-slate-400">kg CO₂eq</span></div>
                {r.emission_source && <div className="text-[10px] text-slate-400 italic">{r.emission_source}</div>}
              </div>
            ) : <span className="text-slate-400 text-xs">Belum diisi</span> },
          { key: "flags", header: "Atribut", render: (r) => (
            <div className="flex flex-wrap gap-1">
              {r.is_low_carbon && <span className="gc-badge bg-emerald-50 text-emerald-700 border border-emerald-200"><Leaf className="w-3 h-3" /> Rendah Karbon</span>}
              {r.is_recycled && <span className="gc-badge bg-cyan-50 text-cyan-700 border border-cyan-200">Daur Ulang</span>}
              {r.is_substitute && <span className="gc-badge bg-amber-50 text-amber-700 border border-amber-200">Substitusi</span>}
            </div>
          )},
        ]}
        extraToolbar={toolbar}
      />

      <ImportDialog
        entity="materials" open={importOpen} onOpenChange={setImportOpen}
        title="Import Bahan Baku"
        description="Unggah file Excel/CSV berisi daftar bahan. Kolom minimal: Kode, Nama, Satuan, Harga Satuan."
        onDone={() => setRefreshKey((k) => k + 1)}
      />
      <ImportDialog
        entity="prices" open={pricesOpen} onOpenChange={setPricesOpen}
        title="Import Harga Bahan"
        description="Perbarui harga bahan secara massal. Sistem mencatat harga lama di riwayat harga."
        onDone={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );
}
